import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import PageMeta from "@/components/PageMeta";

const SESSION_TYPES = ["1:1 Breathwork Session", "Corporate Workshop", "Performance Coaching", "Discovery Call"];

export default function Book() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    session_type: params.get("type") || SESSION_TYPES[0],
    preferred_date: "",
    preferred_time: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name, email: user.email }));
  }, [user]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Booking request submitted.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
          <CheckCircle2 className="h-12 w-12 mx-auto text-primary" />
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-4">Request received.</h1>
          <p className="text-muted-foreground mt-3 max-w-md mx-auto">You'll receive a confirmation email shortly. We typically respond within one business day.</p>
          <div className="mt-8 flex gap-3 justify-center">
            <Button variant="outline" className="rounded-full" onClick={() => setDone(false)} data-testid="book-again-btn">Book another</Button>
            <Button className="rounded-full" onClick={() => navigate(user ? "/dashboard" : "/")} data-testid="book-home-btn">Done</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12 lg:py-16">
      <PageMeta
        title="Book a Session"
        description="Reserve a 1:1 breathwork session, corporate workshop, performance coaching, or a discovery call with Evan Gill."
      />
      <p className="overline text-primary mb-2">Bookings</p>
      <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight">Reserve your session</h1>
      <p className="text-muted-foreground mt-2">Tell us what you need. We'll respond within one business day.</p>

      <Card className="mt-8 sm:mt-10 p-5 sm:p-8">
        <form onSubmit={onSubmit} className="space-y-6" data-testid="booking-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name" id="b-name"><Input id="b-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="book-name-input" /></Field>
            <Field label="Email" id="b-email"><Input id="b-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="book-email-input" /></Field>
            <Field label="Phone" id="b-phone"><Input id="b-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="book-phone-input" /></Field>
            <Field label="Session type" id="b-type">
              <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })}>
                <SelectTrigger id="b-type" data-testid="book-session-type-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SESSION_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`book-session-${t.replace(/\s+/g, '-')}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Preferred date" id="b-date"><Input id="b-date" type="date" required value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="book-date-input" /></Field>
            <Field label="Preferred time" id="b-time"><Input id="b-time" type="time" required value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} data-testid="book-time-input" /></Field>
          </div>
          <Field label="Notes / goals" id="b-notes">
            <Textarea id="b-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="What outcome are you looking for?" data-testid="book-notes-input" />
          </Field>
          <div className="flex justify-end">
            <Button type="submit" size="lg" disabled={submitting} className="w-full sm:w-auto rounded-full" data-testid="book-submit-btn">
              {submitting ? "Submitting..." : "Request booking"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}
